import { Controller } from "@hotwired/stimulus";
import { CalendarScheduler } from "../calendar";

export default class extends Controller {
  static targets = ["calendar", "dateLabel", "viewButton", "appointmentModal"];
  static values = {
    initialDate: String,
    initialView: String,
    dataPath: String,
    locationId: String,
    reschedulePath: String,
    createFormPath: String,
    editFormPath: String,
  };

  connect() {
    this.date = this.initialDateValue;
    this.view = this.initialViewValue || "day";
    this._eventsById = new Map();

    // Client-side filter state (driven by the schedule-filters component).
    // Defaults must match FiltersComponent: completed + cancelled hidden.
    this._allStaff = [];
    this._allEvents = [];
    this.hiddenStaffIds = new Set();
    this.hiddenStatuses = new Set(["completed", "cancelled"]);

    this.calendar = new CalendarScheduler({
      element: this.calendarTarget,
      date: this.date,
      view: this.view,
      staff: [],
      events: [],
      availableViews: ["day", "week", "month"],
    });

    this._bindCalendarEvents();
    this._refreshHeader();

    this._onAppointmentCreated = () => this.refresh();
    document.addEventListener("schedule:appointment-created", this._onAppointmentCreated);

    this._onFiltersChanged = (e) => {
      this.hiddenStaffIds = new Set(e.detail.hiddenStaffIds);
      this.hiddenStatuses = new Set(e.detail.hiddenStatuses);
      this._applyAndPushData();
    };
    document.addEventListener("schedule:filters-changed", this._onFiltersChanged);

    this._dispatchViewChanged();
    this.refresh();
  }

  _dispatchViewChanged() {
    document.dispatchEvent(
      new CustomEvent("schedule:view-changed", { detail: { view: this.view } })
    );
  }

  disconnect() {
    this.calendar?.destroy();
    document.removeEventListener("schedule:appointment-created", this._onAppointmentCreated);
    document.removeEventListener("schedule:filters-changed", this._onFiltersChanged);
  }

  // ---- header actions (invoked via data-action) ----

  setView(event) {
    const view = event.currentTarget.dataset.view;
    if (!view || view === this.view) return;
    this.view = view;
    this.calendar.setView(view);
    this._refreshHeader();
    this._dispatchViewChanged();
    this.refresh();
  }

  previous() { this._shift(-1); }
  next() { this._shift(1); }
  today() { this._setDate(this._formatDate(new Date())); }

  closeAppointmentModal() {
    if (!this.hasAppointmentModalTarget) return;
    this.appointmentModalTarget.innerHTML = "";
    this.appointmentModalTarget.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
  }

  // ---- data fetch ----

  async refresh() {
    const params = new URLSearchParams({ date: this.date, view: this.view });
    if (this.locationIdValue) params.set("location_id", this.locationIdValue);
    const url = `${this.dataPathValue}?${params.toString()}`;
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) {
        this._toast("error", "Failed to load schedule");
        return;
      }
      const payload = await response.json();
      this._allStaff = payload.staff || [];
      this._allEvents = payload.events || [];
      this._eventsById.clear();
      this._allEvents.forEach((e) => this._eventsById.set(String(e.id), e));
      this._applyAndPushData();
    } catch (_) {
      this._toast("error", "Failed to load schedule");
    }
  }

  // Apply the current client-side filters and push to the calendar. Called
  // after every fetch and on every filter change so the state persists
  // across view/date switches.
  _applyAndPushData() {
    // Day view shows every staff as its own column, so the staff filter is
    // irrelevant there — only the status filter applies.
    const dayView = this.view === "day";

    const visibleStaff = dayView
      ? this._allStaff
      : this._allStaff.filter((s) => !this.hiddenStaffIds.has(String(s.id)));

    const visibleEvents = this._allEvents.filter((e) => {
      if (!dayView && this.hiddenStaffIds.has(String(e.staffId))) return false;
      // Blocks are never affected by the status filter.
      if (e.kind === "block") return true;
      return !this.hiddenStatuses.has(e.status);
    });

    this.calendar.setData({ staff: visibleStaff, events: visibleEvents });
  }

  // ---- internal date nav ----

  _shift(direction) {
    const d = this._parseLocalDate(this.date);
    if (this.view === "month") d.setMonth(d.getMonth() + direction);
    else if (this.view === "week") d.setDate(d.getDate() + direction * 7);
    else d.setDate(d.getDate() + direction);
    this._setDate(this._formatDate(d));
  }

  _setDate(newDate) {
    if (newDate === this.date) return;
    this.date = newDate;
    this.calendar.setDate(newDate);
    this._refreshHeader();
    this.refresh();
  }

  _refreshHeader() {
    if (this.hasDateLabelTarget) {
      const d = this._parseLocalDate(this.date);
      const label = d.toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
      this.dateLabelTarget.querySelector("span").textContent = label;
    }

    if (this.hasViewButtonTarget) {
      this.viewButtonTargets.forEach((btn) => {
        const active = btn.dataset.view === this.view;
        btn.classList.toggle("bg-white", active);
        btn.classList.toggle("shadow-sm", active);
        btn.classList.toggle("text-black", active);
        btn.classList.toggle("text-gray-600", !active);
        btn.classList.toggle("hover:text-black", !active);
      });
    }
  }

  // ---- calendar event wiring ----

  _bindCalendarEvents() {
    this.calendar.on("slot-click", (e) => this._onSlotClick(e.detail));
    this.calendar.on("event-click", (e) => this._onEventClick(e.detail));
    this.calendar.on("event-move", (e) => this._onEventMove(e.detail));
    this.calendar.on("event-resize", (e) => this._onEventResize(e.detail));
    this.calendar.on("date-cell-click", (e) => this._onDateCellClick(e.detail));
  }

  _onSlotClick({ staffId, startTime, endTime }) {
    if (!this.hasCreateFormPathValue || !this.createFormPathValue) return;
    const params = new URLSearchParams();
    if (staffId) params.set("staff_id", staffId);
    if (startTime) params.set("start_time", startTime);
    if (endTime) params.set("end_time", endTime);
    if (this.view) params.set("view", this.view);

    const frame = document.getElementById("schedule_quick_modal");
    if (!frame) return;
    frame.src = `${this.createFormPathValue}?${params}`;
  }

  _onEventClick({ kind, id }) {
    if (kind !== "appointment") return;
    if (!this.hasEditFormPathValue || !this.editFormPathValue) return;
    if (!this.hasAppointmentModalTarget) return;

    const url = `${this.editFormPathValue.replace(":id", id)}?from_schedule=1`;
    fetch(url, {
      method: "GET",
      headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
    })
      .then((r) => r.text())
      .then((html) => {
        this.appointmentModalTarget.innerHTML = html;
        this.appointmentModalTarget.classList.remove("hidden");
        document.body.classList.add("overflow-hidden");
      })
      .catch(() => this._toast("error", "Failed to open appointment"));
  }

  async _onEventMove({ id, kind, newStart, newEnd, newStaffId }) {
    if (kind !== "appointment") return;
    const url = this.reschedulePathValue.replace(":id", id);
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this._csrf(),
        Accept: "application/json",
      },
      body: JSON.stringify({ start_time: newStart, end_time: newEnd, staff_id: newStaffId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      this._toast("error", error.error || "Couldn't move appointment");
      this.refresh();
    } else {
      this._toast("success", "Appointment moved");
    }
  }

  async _onEventResize({ id, kind, newEnd }) {
    if (kind !== "appointment") return;

    // Resize emits only newEnd; look up the original start so the backend
    // can re-validate the full range.
    const event = this._eventsById.get(String(id));
    const startTime = event?.start;
    if (!startTime) {
      this._toast("error", "Couldn't resize appointment");
      this.refresh();
      return;
    }

    const url = this.reschedulePathValue.replace(":id", id);
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this._csrf(),
        Accept: "application/json",
      },
      body: JSON.stringify({ start_time: startTime, end_time: newEnd }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      this._toast("error", error.error || "Couldn't resize appointment");
      this.refresh();
    } else {
      this._toast("success", "Appointment resized");
    }
  }

  _onDateCellClick({ date }) {
    if (!date) return;
    this.view = "day";
    this.date = date;
    this.calendar.setView("day");
    this.calendar.setDate(date);
    this._refreshHeader();
    this._dispatchViewChanged();
    this.refresh();
  }

  // ---- helpers ----

  _csrf() {
    return document.querySelector("meta[name=csrf-token]")?.content || "";
  }

  // Build a toast node matching app/views/shared/_toast.html.erb so the
  // existing toast_controller animations + auto-dismiss apply.
  _toast(type, message) {
    const node = document.createElement("div");
    node.className = "fixed top-4 right-4 z-50 max-w-sm bg-white border border-gray-200 rounded-lg shadow-lg p-4 opacity-0 toast-notification";
    node.dataset.controller = "toast";
    node.dataset.toastTypeValue = type;
    node.dataset.action = "animationend->toast#remove";

    const icon = type === "error"
      ? `<svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>`
      : `<svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>`;

    const safe = String(message)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    node.innerHTML = `
      <div class="flex items-center">
        <div class="flex-shrink-0">${icon}</div>
        <div class="ml-3"><p class="text-sm font-medium text-gray-900">${safe}</p></div>
      </div>`;

    document.body.appendChild(node);
  }

  _parseLocalDate(s) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  _formatDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
}
